import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import { RootState, GameData, GamelistModalDetails } from "types";
import {
  setGeoCords,
  setState,
  setCity,
  setCountry,
  setFetchedGeoLoc,
} from "store/actions";
import { getLocation } from "app/Hooks/utils/getLocation";

import GamelistModal from "./components/GamelistModal";
import UnifiedGameList from "./components/UnifiedGameList";

interface GamelistProps {
  setDisplay?: (display: string) => void;
}

function Gamelist({ setDisplay }: GamelistProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const locationState = useSelector((state: RootState) => state.location);
  const { city, state, country } = locationState || {};
  const user = useSelector((state: RootState) => state.user.user);

  const [gameData, setGameData] = useState<GameData>({} as GameData);
  const [modalDets, setModalDets] = useState<GamelistModalDetails>({
    open: false,
    status: "",
  });
  const [requesting, setRequesting] = useState(false);

  // Auto-request location when component mounts (only if user is logged in and location not set)
  useEffect(() => {
    if (user && !country && !requesting) {
      // Don't auto-request immediately, just show the banner
      // User can manually click to enable location
    }
  }, [user, country, requesting]);

  const handleEnableLocation = async () => {
    setRequesting(true);

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 600000,
          });
        },
      );

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      dispatch(setGeoCords({ lng, lat }));
      dispatch(setFetchedGeoLoc());

      try {
        const { city, state, country } = await getLocation(lat, lng);
        dispatch(setCity(city));
        dispatch(setState(state));
        dispatch(setCountry(country));
      } catch (err) {
        console.log("Error setting geolocation details", err);
      }
    } catch (error) {
      console.error("Error getting location:", error);
    } finally {
      setRequesting(false);
    }
  };

  const getLocationStatus = () => {
    if (!country) {
      return {
        message:
          "You must give Local Poker permission to access your location to find great games near you",
        severity: "warning" as const,
        icon: <LocationIcon />,
        showButton: true,
      };
    } else if (country !== "United States") {
      return {
        message: "Local Poker is currently only supported in the United States",
        severity: "info" as const,
        icon: <PublicIcon />,
        showButton: false,
      };
    } else {
      return {
        message: `Games near ${city}, ${state}`,
        severity: "success" as const,
        icon: <LocationIcon />,
        showButton: false,
      };
    }
  };

  const locationStatus = getLocationStatus();

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Location Status */}
      <Box sx={{ mb: 3 }}>
        <Alert
          severity={locationStatus.severity}
          icon={locationStatus.icon}
          action={
            locationStatus.showButton ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleEnableLocation}
                disabled={requesting}
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "#ffffff",
                  color: "#ed6c02",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  "&:disabled": {
                    backgroundColor: "#f5f5f5",
                    color: "#ed6c02",
                  },
                }}
              >
                {requesting ? "Requesting..." : "Enable Location"}
              </Button>
            ) : undefined
          }
          sx={{
            borderRadius: 2,
            "& .MuiAlert-message": {
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: 500,
              flex: 1,
              alignSelf: "center",
            },
          }}
        >
          {locationStatus.message}
        </Alert>
      </Box>

      {/* Unified Game List */}
      <UnifiedGameList 
        setModalDets={setModalDets} 
        setGameData={setGameData}
      />

      {/* Modal */}
      <GamelistModal dets={modalDets} gameInfo={gameData} />
    </Box>
  );
}

export default Gamelist;
